"use client";

import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import HexLive from "@/components/HexLive";

function HexIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 100 115" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <polygon
        points="50,5 95,27.5 95,87.5 50,110 5,87.5 5,27.5"
        stroke="rgb(var(--accent))"
        strokeWidth="2"
        fill="none"
      />
    </svg>
  );
}

interface Principle {
  n: string;
  title: string;
  desc: string;
}

export default function PrinciplesSection() {
  const t = useTranslations("principles");
  const items: Principle[] = t.raw("items") || [];

  return (
    <section className="relative py-24 bg-white overflow-hidden">
      {/* Decorative hexagons */}
      <HexLive className="hidden md:block absolute right-[8%] top-24 w-20 h-20"  mode="pulse" baseOpacity={0.15} intervalSec={42} />
      <HexLive className="hidden md:block absolute right-[15%] bottom-12 w-32 h-32" mode="pulse" baseOpacity={0.10} intervalSec={55} />
      <HexLive className="hidden md:block absolute right-[5%] bottom-32 w-12 h-12"  mode="spark" baseOpacity={0.20} intervalSec={32} />
      <HexLive className="hidden md:block absolute left-[3%] bottom-20 w-16 h-16"  mode="spark" baseOpacity={0.12} intervalSec={38} />

      <div className="relative max-w-7xl mx-auto px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="mb-14 text-start max-w-3xl"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3 tracking-tight">
            {t("title")}
          </h2>
          <p className="text-lg text-slate-500 leading-relaxed">
            {t("sub")}
          </p>
        </motion.div>

        {/* Three principles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((p, i) => (
            <motion.div
              key={p.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="relative flex flex-col gap-4 p-7 rounded-2xl border border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm transition-all overflow-hidden"
            >
              {/* Accent stripe — Solaria blue */}
              <div className="absolute top-0 left-0 right-0 h-1"
                   style={{ background: "rgb(var(--accent))" }} />

              {/* Number + hex */}
              <div className="flex items-center gap-3 pt-2">
                <span className="text-3xl font-mono font-bold tracking-tight"
                
                      style={{ color: "rgb(var(--accent))" }}>
                  {p.n}
                </span>
                <HexIcon className="w-6 h-6 opacity-30" />
              </div>

              {/* Title */}
              <h3 className="text-xl font-semibold text-slate-900 leading-snug">
                {p.title}
              </h3>

              {/* Description */}
              <p className="text-[14px] text-slate-500 leading-relaxed">
                {p.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}